import microdiff from 'microdiff';

export const useSidechainStore = defineStore('sidechain', {
  state: () => {
    return {
      isChatOpen: false,
      currentChatId: null,
      messageToSend: null,
      chats: [],
      messages: [],
      isChatCreating: false,
      pendingApiCalls: [],
      status: null,
      isLatestChatLoading: false,
      suggestionsRefetchSignal: 0,
    };
  },

  actions: {
    async fetchChats() {
      const response = await useApi.get('/sidechain/chats')
        .catch((error) => {
          console.error(error);
          useToast.error(useT('common.errorLoadingData'));
        });

      this.chats = response;

      return response;
    },

    async fetchMessages() {
      const response = await useApi.get(`/sidechain/chats/${this.currentChatId}/messages`)
        .catch((error) => {
          console.error(error);
          useToast.error(useT('common.errorLoadingData'));
        });

      if (response) {
        this.messages = response;
      }

      return response;
    },

    async createChat({ message }) {
      const response = await useApi.post('/sidechain/chat', {
        body: {
          title: 'New conversation',
          lastMessage: {
            role: 'user',
            content: message,
          },
        },
      }).catch((error) => {
        console.error(error);
        useToast.error(useT('common.errorLoadingData'));
      });

      if (response) {
        this.currentChatId = response._id;
        this.chats.unshift(response);
      }

      console.log('createChat', response);

      return response;
    },

    async createChatAndSendMessage(payload) {
      this.isChatCreating = true;
      this.currentChatId = null;
      this.messages = [];
      this.isChatOpen = true;

      const chat = await this.createChat(payload);

      if (chat) {
        this.messageToSend = {
          chatId: chat._id,
          ...payload,
        };
      } else {
        this.isChatOpen = false;
      }

      this.isChatCreating = false;
    },

    async rejectToolCalls({ messageId }) {
      const response = await useApi.post('/sidechain/reject-tool-calls', {
        body: {
          messageId,
        },
      });

      return response;
    },

    getContextDiffs({ messages, context }) {
      const initialContextPart = (messages || [])
        ?.flatMap((message) => message.parts)
        ?.findLast((part) => part.type === 'text' && part.text?.includes('<initialContext>'));

      const initialContext = initialContextPart ? JSON.parse(initialContextPart.text.replace('<initialContext>', '').replace('</initialContext>', '')) : {};

      const contextUpdateParts = (messages || [])
        ?.flatMap((message) => message.parts)
        ?.filter((part) => part.type === 'text' && part.text?.includes('<contextUpdate>'))
        ?.flatMap((part) => JSON.parse(part.text.replace('<contextUpdate>', '').replace('</contextUpdate>', '') || '[]'));

      // Apply diffs to initial context output
      const computedContext = _cloneDeep(initialContext);
      contextUpdateParts.forEach((action) => {
        if (action.type === 'create') {
          _set(computedContext, action.path, action.value);
        } else if (action.type === 'change') {
          _set(computedContext, action.path, action.value);
        } else if (action.type === 'remove') {
          _unset(computedContext, action.path);
        }
      });

      const diffs = microdiff(computedContext, _cloneDeep(context));
      const diffsParsed = diffs.map((item) => ({
        type: item.type?.toLowerCase(),
        path: item.path?.join('.'),
        value: item.value,
      }));

      return diffsParsed;
    },

    async openLatestChat() {
      this.isChatOpen = true;
      this.currentChatId = this.chats?.[0]?._id;

      if (!this.currentChatId) {
        this.isLatestChatLoading = true;
      }

      const response = await useApi.get('/sidechain/chats', {
        query: {
          limit: 1,
        },
      });

      this.isLatestChatLoading = false;

      if (response.length > 0 && this.currentChatId !== response[0]._id) {
        this.currentChatId = response[0]._id;
      }
    },
  },

  getters: {
    isStoppable: (state) => Boolean(state.status === 'streaming' || state.pendingApiCalls?.length),
    planProcessMessage: (state) => state.messages.find((message) => message.parts.some((part) => part.type === 'tool-planProcess')),
    isOneShotInProgress: (state) => {
      // return true; // TODO: remove this
      const isNotReady = Boolean(state.status !== 'ready' || state.pendingApiCalls?.length);
      const lastAssistantMessage = (state.messages || []).findLast((message) => message.role === 'assistant');
      const hasLastMessagePlanProcessToolCall = Boolean(lastAssistantMessage?.parts?.some((part) => part.type === 'tool-planProcess'));
      const lastPartIsToolCall = Boolean(_last(_last(state.messages)?.parts)?.type?.includes('tool-'));

      return Boolean(
        (isNotReady || lastPartIsToolCall)
        && state.status !== 'error'
        && useSidechainStore().planProcessMessage
        && hasLastMessagePlanProcessToolCall
        && useSidechainStore().isChatOpen,
      );
    },
    isChatOpenNotProgress: () => {
      if (useSidechainStore().isOneShotInProgress && !grid.lg) {
        return false;
      }

      return useSidechainStore().isChatOpen;
    },
  },
});
